import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import Close from '@/assets/icons/close.svg';

import { _searchDelay } from '@/helpers/floor-plan/floor-plan-constants';
import { isBrowser } from '@/helpers/is-browser';
import { useOnClickOutside } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { IMeetingplacePageHome } from '@/lib/fragments/meetingPlacePageHome';
import { ClickHistory } from '../floor-plan/floor-plan';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import {
  ActiveBuilding,
  FilterTile,
  MapTile,
} from '../floor-plan-map/floor-plan-map.interface';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import { InputFieldTrailingIcon } from '../ui/input-field/input-field';
import { FloorPlanFilterContent } from './floor-plan-filter-content';
import { FloorPlanFilterListStyle } from './floor-plan-filter-list-style.type';
import { FloorPlanFilterListType } from './floor-plan-filter-list-type.type';
import { useUser } from '@/providers/UserProvider';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';

interface FloorPlanFilterProps {
  activeTiles: FilterTile[];
  listStyle: FloorPlanFilterListStyle;
  backToList?: () => void;
  performListFiltering?: (filterVal: string) => void;
  performCategoryListing?: (
    filterCategory: string,
    type?: FloorPlanFilterListType,
  ) => void;
  updateFilterList?: React.Dispatch<
    React.SetStateAction<FloorPlanFilterListType>
  >;
  activeStore: MapTile | undefined;
  activeBuilding: ActiveBuilding | undefined;
  setActiveStore: Dispatch<SetStateAction<MapTile | undefined>>;
  setClickHistory: Dispatch<SetStateAction<ClickHistory[]>>;
  clickHistory: ClickHistory[];
  clickOnTenant: (tile: FilterTile) => Promise<void>;
  clearAllMarkedPlaces: () => void;
  goToFloor: (id?: string) => Promise<void>;
  checkFloorOfID: (id: string) => string | false | undefined;
  getLocalizedShortName: (id: string) => string | false | undefined;
  homepage: IMeetingplacePageHome;
}

export const FloorPlanFilter = ({
  activeTiles,
  listStyle,
  performListFiltering,
  performCategoryListing,
  updateFilterList,
  activeStore,
  setActiveStore,
  setClickHistory,
  clickHistory,
  clickOnTenant,
  clearAllMarkedPlaces,
  goToFloor,
  checkFloorOfID,
  getLocalizedShortName,
  homepage,
  activeBuilding,
}: FloorPlanFilterProps) => {
  const isLivliFamily = useIsMPLivliFamily();

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [filterFieldIcon, setFilterFieldIcon] =
    useState<InputFieldTrailingIcon>('Search');
  const [filterValue, setFilterValue] = useState<string>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const debouncedFilterValue = useDebounce(filterValue, _searchDelay);
  const debouncedGtmFilterValue = useDebounce(filterValue, _searchDelay * 3);
  const isMobile = useIsMobile();

  const filterRef = useRef<HTMLDivElement>(null);

  const { user } = useUser();

  // Restore state from URL params on mount
  useEffect(() => {
    if (isInitialized) return;

    const searchParam = searchParams.get('fpSearch');
    const categoryParam = searchParams.get('fpCategory');
    const categoryTypeParam = searchParams.get(
      'fpCategoryType',
    ) as FloorPlanFilterListType | null;

    if (searchParam) {
      setFilterValue(searchParam);
      performListFiltering?.(searchParam);
    }

    if (categoryParam && categoryTypeParam && performCategoryListing) {
      performCategoryListing(categoryParam, categoryTypeParam);
      // Restore click history from category
      if (updateFilterList) {
        updateFilterList(categoryTypeParam);
      }

      setTimeout(() => {
        performCategoryListing(categoryParam, categoryTypeParam);
        setClickHistory([{ title: categoryParam, type: categoryTypeParam }]);

        setFilterDrawerOpen(true);
        setFilterFieldIcon('ClearButton');
      }, 100);
    }

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL params when filter value changes
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(searchParams.toString());

    if (debouncedFilterValue) {
      params.set('fpSearch', debouncedFilterValue);
      // Clear category when searching
      params.delete('fpCategory');
      params.delete('fpCategoryType');
    } else {
      params.delete('fpSearch');
    }

    const newUrl = `${pathname}?${params.toString()}`;
    router.replace(newUrl, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValue, isInitialized]);

  useEffect(() => {
    if (debouncedFilterValue.length > 0) {
      setFilterFieldIcon('ClearButton');
      setFilterDrawerOpen(true);
    } else {
      setFilterFieldIcon('Search');
    }
    performListFiltering?.(debouncedFilterValue);
    // Clear category when searching
    // if (debouncedFilterValue && isInitialized) {
    setClickHistory([]);
    //   const params = new URLSearchParams(searchParams.toString());
    //   params.delete('fpCategory');
    //   params.delete('fpCategoryType');
    //   const newUrl = `${pathname}?${params.toString()}`;
    //   router.replace(newUrl, { scroll: false });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValue]);

  // Wrapper for performCategoryListing that updates URL
  const handleCategoryListing = (
    filterCategory: string,
    type?: FloorPlanFilterListType,
  ) => {
    performCategoryListing?.(filterCategory, type);

    if (isInitialized) {
      const params = new URLSearchParams(searchParams.toString());
      if (filterCategory) {
        params.set('fpCategory', filterCategory);
        if (type) {
          params.set('fpCategoryType', type);
        }
      } else {
        params.delete('fpCategory');
        params.delete('fpCategoryType');
      }
      // Clear search when selecting category
      params.delete('fpSearch');
      const newUrl = `${pathname}?${params.toString()}`;
      router.replace(newUrl, { scroll: false });
    }
  };

  useEffect(() => {
    if (debouncedGtmFilterValue.length > 0) {
      //LW-1743
      sendGtmEvent(debouncedGtmFilterValue);
    }
  }, [debouncedGtmFilterValue]);

  const sendGtmEvent = (searchTerm: string) => {
    if (isBrowser) {
      dataLayer.push({
        event: 'map_search',
        search_term: searchTerm,
        search_category: 'No search Filter. Map search used',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  };
  useEffect(() => {
    if (filterValue !== '') return;
    if (clickHistory.length > 0) {
      setFilterFieldIcon('ClearButton');
    } else if (clickHistory.length === 0) {
      setFilterFieldIcon('Search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickHistory]);

  const inputFocus = async () => {
    clearAllMarkedPlaces();
    await goToFloor();
    setActiveStore(undefined);
    setFilterDrawerOpen(true);
    if (clickHistory.length === 0 && filterValue === '')
      performListFiltering?.('');
  };

  const handleBackClick = async () => {
    clearAllMarkedPlaces();

    setFilterValue('');
    performListFiltering?.('');

    const params = new URLSearchParams(searchParams.toString());

    params.delete('fpSearch');
    params.delete('fpCategory');
    params.delete('fpCategoryType');
    if (clickHistory.length > 1) {
      const firstItemType = clickHistory.slice(0, 1)[0].type;
      if (firstItemType && updateFilterList) {
        updateFilterList(firstItemType);
        await goToFloor();
      }
      setClickHistory([...clickHistory.slice(0, -1)]);
    } else {
      setClickHistory([]);
    }
    const newUrl = `${pathname}?${params.toString()}`;
    router.replace(newUrl, { scroll: false });

    // if (clickHistory.length > 1) {
    //   const previousHistory = clickHistory.slice(0, -1);
    //   const previousItem = previousHistory[previousHistory.length - 1];

    //   // Update URL params to reflect previous state
    //   const params = new URLSearchParams(searchParams.toString());

    //   if (previousItem) {
    //     // Restore previous category
    //     params.set('fpCategory', previousItem.title);
    //     if (previousItem.type) {
    //       params.set('fpCategoryType', previousItem.type);
    //     }
    //     params.delete('fpSearch');

    //     if (previousItem.type && updateFilterList) {
    //       updateFilterList(previousItem.type);
    //     }
    //     performCategoryListing?.(previousItem.title, previousItem.type);
    //   } else {
    //     // No more history, clear all filters
    //     params.delete('fpSearch');
    //     params.delete('fpCategory');
    //     params.delete('fpCategoryType');
    //     setFilterValue('');
    //     performListFiltering?.('');
    //   }

    //   const newUrl = `${pathname}?${params.toString()}`;
    //   router.replace(newUrl, { scroll: false });

    //   setClickHistory(previousHistory);
    //   setActiveStore(undefined);
    //   await goToFloor();
    // } else {
    //   // Clear all state
    //   const params = new URLSearchParams(searchParams.toString());
    //   params.delete('fpSearch');
    //   params.delete('fpCategory');
    //   params.delete('fpCategoryType');
    //   const newUrl = `${pathname}?${params.toString()}`;
    //   router.replace(newUrl, { scroll: false });

    //   setFilterValue('');
    //   performListFiltering?.('');
    //   setClickHistory([]);
    // }
  };

  const handleClickOnTenant = async (tile: FilterTile) => {
    if (isMobile && ((tile.floorPlanIDs && tile.floorPlanIDs?.length > 0) || (tile?.unitId && tile?.unitId?.length > 0))) {
      setFilterDrawerOpen(false);
    }
    await clickOnTenant(tile);
  };

  const handleClearField = () => {
    setFilterValue('');
    setClickHistory([]);
    performCategoryListing?.('');

    if (isInitialized) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('fpSearch');
      params.delete('fpCategory');
      params.delete('fpCategoryType');
      const newUrl = `${pathname}?${params.toString()}`;
      router.replace(newUrl, { scroll: false });
    }
  };

  const handleXClick = () => {
    setFilterDrawerOpen(false);
    // Clear all filters and URL params
    if (isInitialized) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('fpSearch');
      params.delete('fpCategory');
      params.delete('fpCategoryType');
      const newUrl = `${pathname}?${params.toString()}`;
      router.replace(newUrl, { scroll: false });
    }
    setFilterValue('');
    performListFiltering?.('');
    setClickHistory([]);
  };

  useEffect(() => {
    if (isMobile) {
      filterDrawerOpen
        ? document.body.classList.add('backdrop')
        : document.body.classList.remove('backdrop');
    }
  }, [filterDrawerOpen, isMobile]);

  const blockHandler = clickHistory.length > 0 || filterValue !== '';

  useOnClickOutside(filterRef, handleXClick, blockHandler);

  return (
    <>
      <div
        className={`${
          isMobile && filterDrawerOpen ? 'absolute' : 'opacity-0'
        } -bottom-28 -top-36 left-0 right-0 ${
          isMobile ? 'z-50' : 'z-20'
        } bg-background-primary-light bg-dynamicStrokeLargeGrey bg-contain bg-no-repeat transition-opacity duration-1000 ease-out md:-bottom-40 md:-left-8 md:-right-8 md:-top-40 md:overflow-hidden`}
      >
        <div
          className={`absolute left-6 top-8 ${
            filterDrawerOpen ? '' : 'hidden'
          }`}
        >
          <ImageWrapper
            className="w-36"
            alt={
              homepage?.meetingPlace?.logo?.description
                ? homepage.meetingPlace.logo.description
                : ''
            }
            url={homepage?.meetingPlace?.logo?.url}
            width={144}
            height={
              homepage?.meetingPlace?.logo?.height &&
              parseInt(homepage?.meetingPlace?.logo?.height) < 60
                ? homepage?.meetingPlace?.logo?.heightt
                : 60
            }
          />
          <div className="mt-3 font-title text-headingxs text-text-default lg:hidden">
            {homepage?.meetingPlace?.name}
          </div>
        </div>
        {filterDrawerOpen && (
          <button
            className={`absolute right-6 top-8 ${
              isMobile ? 'z-50' : 'z-20'
            } flex h-12 w-12 items-center justify-center rounded-full bg-brand-white lg:hidden`}
            onClick={handleXClick}
          >
            <Close />
          </button>
        )}
      </div>
      <div
        ref={filterRef}
        className={`scrollbar-mt absolute top-4 ${
          isMobile && filterDrawerOpen ? 'z-50' : 'z-10'
        } overflow-hidden rounded-none p-2 lg:left-9 lg:top-8 lg:w-[380px] lg:rounded-xl lg:bg-background-primary-light lg:bg-dynamicStrokeLargeGrey lg:bg-cover lg:pl-6 lg:pr-6 lg:pt-6 lg:shadow-default ${
          filterDrawerOpen && !isMobile
            ? ' bottom-0 left-0 right-0 bg-background-primary-light bg-dynamicStrokeLargeGrey pt-4 lg:absolute lg:bottom-9'
            : ' left-4 right-4 pb-6'
        }`}
      >
        <FloorPlanFilterContent
          autoFocusInput={false}
          filterValue={filterValue}
          clickHistory={clickHistory}
          handleBackClick={handleBackClick}
          filterFieldIcon={filterFieldIcon}
          setFilterValue={setFilterValue}
          inputFocus={inputFocus}
          filterDrawerOpen={filterDrawerOpen}
          listStyle={listStyle}
          activeTiles={activeTiles}
          updateFilterList={updateFilterList}
          activeStore={activeStore}
          activeBuilding={activeBuilding}
          setActiveStore={setActiveStore}
          setClickHistory={setClickHistory}
          clickOnTenant={handleClickOnTenant}
          goToFloor={goToFloor}
          checkFloorOfID={checkFloorOfID}
          getLocalizedShortName={getLocalizedShortName}
          performCategoryListing={handleCategoryListing}
          setFilterDrawerOpen={setFilterDrawerOpen}
          homepage={homepage}
          node_locale={homepage?.sys?.locale}
          handleClearField={handleClearField}
        />
      </div>
    </>
  );
};
